// 인쇄용 QR — 실물(책·엽서·굿즈·행사물)에 붙여 스캔 시 지정 웹 링크로 진입시킨다(FR-QR).
// AR 페이지·책 전체·굿즈 상세 등 어디든 재사용. 출처는 웹(contentBase) — localhost 면 배포 후 사용.
import { useRef } from "react";
import { isLocalHostUrl } from "@greg-chick/shared";
import { QRCodeCanvas } from "qrcode.react";
import { contentBase } from "../lib/contentApi";

interface Props {
  path: string; // 웹 경로(예: /ar?scene=..., /book?id=..., /goods/xxx)
  title: string; // 인쇄물 제목(예: "3쪽 AR", "그레그와 병아리")
  caption?: string; // 스캔 안내 문구
  size?: number;
  /**
   * 🔴 **주소를 종이에 안 적는다.**
   *
   * 현장 QR 처럼 주소에 비밀 토큰이 들어가는 경우, 그 긴 주소를 표지판에 글자로 찍어 두면
   * 지나가는 사람이 옮겨 적을 수 있다(위치 확인이 또 있어서 그것만으로 도장을 받진 못하지만,
   * 굳이 적어 둘 이유가 없다). 스캔에는 QR 만 있으면 된다.
   */
  hideUrl?: boolean;
}

export function QrPrint({
  path,
  title,
  caption = "휴대폰 카메라로 스캔하세요",
  size = 112,
  hideUrl = false,
}: Props) {
  const boxRef = useRef<HTMLDivElement>(null);
  const base = contentBase();
  const url = `${base}${path}`;
  // 🔴 종이에 찍혀 나가는 자리다 — 판정은 shared 한 곳(`0.0.0.0`·`[::1]` 도 안다)
  const isLocal = isLocalHostUrl(base);

  function print() {
    const canvas = boxRef.current?.querySelector("canvas");
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const w = window.open("", "_blank", "width=420,height=560");
    if (!w) return;
    /**
     * 🔴 **이어 붙이는 글자는 이스케이프한다**(2026-10-11). 이 창은 관리자와 **같은 출처**(about:blank)라
     * 여기서 스크립트가 돌면 관리자 열쇠에 닿는다. 지금 제목은 운영자가 넣은 것뿐이지만(책·굿즈·랠리 지점 이름),
     * `<`·`&` 가 든 이름이면 인쇄 화면이 깨지고, 바깥 입력이 제목으로 들어오는 날 바로 구멍이 된다.
     */
    const esc = (v: string) =>
      v.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
    w.document.write(
      `<html><head><title>QR · ${esc(title)}</title><style>` +
        `body{font-family:sans-serif;text-align:center;padding:24px;color:#222}` +
        `img{width:300px;height:300px}h2{margin:8px 0 2px}p{color:#666;font-size:13px;word-break:break-all}` +
        `</style></head><body>` +
        `<h2>${esc(title)}</h2><p>${esc(caption)}</p>` +
        `<img src="${esc(dataUrl)}" alt="QR"/>` +
        (hideUrl ? "" : `<p>${esc(url)}</p>`) +
        `</body></html>`,
    );
    w.document.close();
    w.focus();
    w.print();
  }

  return (
    <div className="qrp">
      <div className="qrp__box" ref={boxRef}>
        <QRCodeCanvas value={url} size={size} level="M" marginSize={2} />
      </div>
      <div className="qrp__info">
        <b>인쇄용 QR</b> — 스캔하면 <b>{title}</b>(으)로 바로 들어가요.
        {hideUrl ? (
          <code className="qrp__url">비밀 토큰이 들어 있어요 — 종이에는 QR 만 찍혀요</code>
        ) : (
          <code className="qrp__url">{url}</code>
        )}
        <div className="qrp__actions">
          <button type="button" className="btn btn--sm" onClick={print}>🖨️ QR 인쇄</button>
          <button type="button" className="btn btn--sm" onClick={() => void navigator.clipboard?.writeText(url)}>링크 복사</button>
        </div>
        {isLocal ? (
          <p className="zone__tip">
            지금 주소가 <code>localhost</code>라 휴대폰 스캔은 안 돼요. 배포 후(실도메인) 인쇄하세요.
            테스트는 폰에서 위 링크의 localhost 를 PC IP 로 바꿔 여세요.
          </p>
        ) : null}
      </div>
    </div>
  );
}
